import type { PrincipalIdentifierLocal } from '../api/types/PlatformLocal';
import { useMyProfile } from './useMyProfile';

/**
 * DEC-RBAC-079/081: resuelve el identificador de login del PROPIO tenant del usuario logueado (con qué
 * dato inician sesión sus usuarios y si admite un secundario opcional).
 *
 * Gemelo de `useTenantIdentifier`, pero para el contexto TENANT-ADMIN, que NO tiene `getTenant`
 * (platform). Lee `meApi.getProfile()` (`GET /me/profile`), donde el backend expone
 * `principalIdentifier`/`allowSecondary` del tenant del usuario. Misma forma de retorno y mismos
 * defaults de back-compat que `useTenantIdentifier`:
 *   - `principalIdentifier` ausente → `'EMAIL'`
 *   - `allowSecondary` ausente → `false`
 *
 * Mientras el perfil carga devuelve los defaults, así el consumidor siempre tiene un shape estable.
 *
 * También expone `email`/`phone` del propio perfil (misma query, sin duplicar el fetch) para que el
 * consumidor pueda derivar el VALOR ACTUAL del secundario (el opuesto al principal) y saber si el
 * usuario ya lo configuró (write-on-verify ⇒ si está en el perfil, ya está verificado).
 */
export function useMyTenantIdentifier(): {
    principalIdentifier: PrincipalIdentifierLocal;
    allowSecondary: boolean;
    email?: string;
    phone?: string;
    /** DEC-RBAC-045: 2FA on/off del tenant. `false` ⇒ ocultar la tarjeta de MFA. `undefined` ⇒ mostrar. */
    mfaEnabled?: boolean;
} {
    const { data } = useMyProfile();

    return {
        principalIdentifier: data?.principalIdentifier ?? 'EMAIL',
        allowSecondary: data?.allowSecondary ?? false,
        email: data?.email,
        phone: data?.phone,
        mfaEnabled: data?.mfaEnabled,
    };
}
